import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bars3Icon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { Program } from "../../services/api";

interface SortableProgramRowProps {
  program: Program;
  index: number;
  onEdit: (programId: string, e?: React.MouseEvent) => void;
  onToggleVisibility: (programId: string, e?: React.MouseEvent) => void;
  onRowClick: (program: Program) => void;
  isDraggable?: boolean;
}

const SortableProgramRow: React.FC<SortableProgramRowProps> = ({
  program,
  index,
  onEdit,
  onToggleVisibility,
  onRowClick,
  isDraggable = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: program._id, disabled: !isDraggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasCashback =
    program.cashbackType === "fixed"
      ? !!program.cashbackFixedAmount
      : !!program.cashbackRate;
  const isIncomplete =
    !program.descriptionCustomer ||
    !program.conditions ||
    !hasCashback ||
    !program.countries?.length;
  const isAwinOnly = program.affiliateUrl && isIncomplete;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
        isAwinOnly
          ? "bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500"
          : ""
      }`}
      onClick={() => onRowClick(program)}
    >
      {/* Drag Handle */}
      <td className="px-6 py-4 whitespace-nowrap">
        {isDraggable ? (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={(e) => e.stopPropagation()}
          >
            <Bars3Icon className="h-5 w-5" />
          </div>
        ) : (
          <div className="w-5 h-5" /> // Placeholder to keep alignment
        )}
      </td>

      {/* Priority */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
          {index + 1}
        </span>
      </td>

      {/* Program */}
      <td className="px-6 py-4">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <img
              className="h-10 w-10 rounded-full object-cover"
              src={program.logo}
              alt={program.name}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                target.nextElementSibling?.classList.remove("hidden");
              }}
            />
            <div className="hidden h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300">
              {program.name.charAt(0)}
            </div>
          </div>
          <div className="ml-4">
            <div className="flex items-center space-x-2">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {program.name}
              </div>
              {isAwinOnly && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                  Setup needed
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* Category */}
      <td className="px-6 py-4 whitespace-nowrap">
        {program.category ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
            {typeof program.category === "object" && program.category !== null
              ? (program.category as any).name ||
                (program.category as any).categoryName ||
                "Category"
              : program.category}
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            No category
          </span>
        )}
      </td>

      {/* Cashback Rate */}
      <td className="px-6 py-4 whitespace-nowrap">
        {program.cashbackType === "fixed" ? (
          program.cashbackFixedAmount ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              {program.cashbackFixedAmount} EP
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
              Not set
            </span>
          )
        ) : program.cashbackRate ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
            {program.cashbackRate}%
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
            Not set
          </span>
        )}
      </td>

      {/* Countries */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {program.countries?.length ? (
          <>
            {program.countries.slice(0, 3).join(", ")}
            {program.countries.length > 3 &&
              ` +${program.countries.length - 3}`}
          </>
        ) : (
          <span className="text-orange-600 dark:text-orange-400 italic">
            Not set
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            program.visibility
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"
          }`}
        >
          {program.visibility ? "● Active" : "○ Hidden"}
        </span>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(program._id, e);
            }}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            title={program.visibility ? "Hide" : "Show"}
          >
            {program.visibility ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(program._id, e);
            }}
            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default SortableProgramRow;
